import React, { useState, useEffect, useCallback } from 'react';
import { useAuth, clearSessionIf401 } from '../../hooks/useAuth';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface CredentialSettings {
  azure_openai_endpoint?: string;
  azure_openai_key?: string;
  azure_openai_deployment?: string;
  azure_openai_api_version?: string;
  google_gemini_api_key?: string;
  google_gemini_model?: string;
  anthropic_api_key?: string;
  anthropic_model?: string;
}

interface AgentConfig {
  agent: string;
  provider?: 'azure_openai' | 'google_gemini' | 'anthropic';
  model?: string;
  temperature?: number;
  max_tokens?: number;
  anonymize_level?: 'off' | 'structured_only' | 'full';
  anonymize_debug?: boolean;
  partial?: boolean;
}

interface TokenStats {
  total: {
    promptTokens: number;
    completionTokens: number;
    totalTokens: number;
    calls: number;
    avgDurationMs: number;
    uptimeMinutes: number;
  };
  perMinute: {
    tokensPerMinute: number;
    callsPerMinute: number;
    promptTokensPerMinute: number;
    completionTokensPerMinute: number;
  };
  lastCall: {
    timestamp: number;
    promptTokens: number;
    completionTokens: number;
    totalTokens: number;
    provider: string;
    model: string;
    durationMs: number;
  } | null;
  byProviderModel?: any[];
}

interface PromptTextEntry {
  text: string;
  variables: string[];
}

interface IntentGroup {
  description?: string;
  keywords?: string[];
  patterns?: string[];
  regex_patterns?: Record<string, string>;
  team_keywords_extra?: string[];
  false_positives?: string[];
  exclude_words?: string[];
  partial_match_patterns?: Record<string, string>;
}

interface IntentConfig {
  intents: Record<string, IntentGroup>;
  stopwords: string[];
}

interface IntentTestResult {
  fired: boolean;
  confidence: number;
  matched: string[];
}

interface AllowedUserRow {
  id: number;
  email: string;
  name: string | null;
  role: 'admin' | 'hr' | 'user';
  is_active: number;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

type TabId = 'config' | 'users' | 'prompts' | 'intents';

const RAW_API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3002';
const API_BASE = RAW_API_BASE.replace(/\/$/, '').endsWith('/api')
  ? RAW_API_BASE.replace(/\/$/, '')
  : `${RAW_API_BASE.replace(/\/$/, '')}/api`;

const INPUT_CLS =
  'w-full px-4 py-2 bg-dark-bg border border-dark-border rounded-lg text-dark-text placeholder-dark-text-secondary focus:outline-none focus:ring-2 focus:ring-brand-blue';
const LABEL_CLS = 'block text-sm font-medium text-dark-text mb-2';
const HINT_CLS = 'text-xs text-dark-text-secondary mt-1';
const CARD_CLS = 'bg-dark-card border border-dark-border rounded-xl shadow-sm p-6';

const PROVIDER_OPTIONS: { value: string; label: string }[] = [
  { value: 'azure_openai', label: 'Azure OpenAI' },
  { value: 'google_gemini', label: 'Google Gemini' },
  { value: 'anthropic', label: 'Anthropic' },
];

const ANON_OPTIONS: { value: string; label: string }[] = [
  { value: 'off', label: 'off' },
  { value: 'structured_only', label: 'structured_only' },
  { value: 'full', label: 'full' },
];

const MODELS_BY_PROVIDER: Record<string, { value: string; label: string }[]> = {
  azure_openai: [
    { value: 'gpt-5-chat', label: 'GPT-5.2 Chat Feb 2026 (via gpt-5-chat deployment)' },
    { value: 'gpt-5-chat', label: 'GPT-5.2 (via gpt-5-chat deployment)' },
    { value: 'gpt-5-chat', label: 'GPT-5.1 (via gpt-5-chat deployment)' },
    { value: 'gpt-5-chat', label: 'GPT-5 Chat (via gpt-5-chat deployment)' },
    { value: 'gpt-5-chat', label: 'GPT-5 (via gpt-5-chat deployment)' },
    { value: 'gpt-5-chat', label: 'GPT-5 Mini (via gpt-5-chat deployment)' },
    { value: 'gpt-5-chat', label: 'GPT-5 Nano (via gpt-5-chat deployment)' },
    { value: 'gpt-5-chat', label: 'GPT-5 Mini Lite (via gpt-5-chat deployment)' },
    { value: 'gpt-5-chat', label: 'GPT-4.1 (via gpt-5-chat deployment)' },
    { value: 'gpt-5-chat', label: 'GPT-4.1 Mini (via gpt-5-chat deployment)' },
    { value: 'gpt-5-chat', label: 'GPT-4.1 Nano (via gpt-5-chat deployment)' },
    { value: 'gpt-5-chat', label: 'o4-mini (via gpt-5-chat deployment)' },
    { value: 'gpt-5-chat', label: 'o3 (via gpt-5-chat deployment)' },
    { value: 'gpt-5-chat', label: 'o3-mini (via gpt-5-chat deployment)' },
    { value: 'gpt-5-chat', label: 'o1 (via gpt-5-chat deployment)' },
    { value: 'gpt-5-chat', label: 'GPT-4o (via gpt-5-chat deployment)' },
    { value: 'gpt-5-chat', label: 'GPT-4o Mini (via gpt-5-chat deployment)' },
  ],
  google_gemini: [
    { value: 'gemini-2.5-flash', label: 'Gemini 2.5 Flash (recommended)' },
    { value: 'gemini-2.5-pro', label: 'Gemini 2.5 Pro' },
    { value: 'gemini-2.0-flash', label: 'Gemini 2.0 Flash' },
    { value: 'gemini-1.5-pro', label: 'Gemini 1.5 Pro' },
    { value: 'gemini-1.5-flash', label: 'Gemini 1.5 Flash' },
  ],
  anthropic: [
    { value: 'claude-opus-4-6', label: 'Claude Opus 4.6 (recommended)' },
    { value: 'claude-sonnet-4-6', label: 'Claude Sonnet 4.6' },
    { value: 'claude-opus-4-5-20251101', label: 'Claude Opus 4.5' },
    { value: 'claude-sonnet-4-5-20250929', label: 'Claude Sonnet 4.5' },
    { value: 'claude-haiku-4-5-20251001', label: 'Claude Haiku 4.5' },
    { value: 'claude-opus-4-1-20250805', label: 'Claude Opus 4.1' },
  ],
};

const TABLE_INPUT =
  'w-full px-2 py-1 text-sm bg-dark-bg border border-dark-border rounded text-dark-text placeholder-dark-text-secondary focus:outline-none focus:ring-1 focus:ring-brand-blue';
const TABLE_SELECT =
  'px-2 py-1 text-sm bg-dark-bg border border-dark-border rounded text-dark-text focus:outline-none focus:ring-1 focus:ring-brand-blue';

const PROMPT_CATEGORIES: Record<string, string[]> = {
  Chat: ['chat-main', 'chat-system'],
  Extraction: ['search-brief', 'team-context-extraction', 'position-extraction'],
  Scoring: ['candidate-scoring', 'team-strength-scoring', 'ai-scoring-system', 'semantic-match'],
  Team: ['team-orchestrator', 'team-summary', 'team-response'],
  Partials: [],
};

const BTN_PRIMARY = 'px-4 py-2 rounded-lg text-sm font-medium bg-brand-blue hover:bg-brand-blue/80 text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed';
const BTN_SECONDARY = 'px-4 py-2 rounded-lg text-sm font-medium bg-dark-bg border border-dark-border text-dark-text-secondary hover:text-dark-text hover:border-dark-text-secondary transition-colors disabled:opacity-50';

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const AdminSettings: React.FC = () => {
  const { token } = useAuth();

  // --- Tab state ---
  const [activeTab, setActiveTab] = useState<TabId>('config');

  // --- Credentials state ---
  const [creds, setCreds] = useState<CredentialSettings>({});
  const [credChanges, setCredChanges] = useState<Partial<CredentialSettings>>({});
  const [lastUpdated, setLastUpdated] = useState('');
  const [updatedBy, setUpdatedBy] = useState('');

  // --- Public URLs (DB-backed; env vars override when set on server) ---
  const [publicSite, setPublicSite] = useState({ frontend_public_url: '', backend_public_url: '' });
  const [savingSiteUrls, setSavingSiteUrls] = useState(false);

  // --- Agent config state ---
  const [agents, setAgents] = useState<Record<string, AgentConfig>>({});
  const [agentChanges, setAgentChanges] = useState<Record<string, Partial<AgentConfig>>>({});
// @ts-ignore
  const [agentCount, setAgentCount] = useState(0);
  const [agentsLoading, setAgentsLoading] = useState(false);
  const [lastReload, setLastReload] = useState<string | null>(null);
  const [reloading, setReloading] = useState(false);
  const [savingAgents, setSavingAgents] = useState(false);

  // --- Token usage state ---
  const [tokenStats, setTokenStats] = useState<TokenStats | null>(null);
  const [tokenLoading, setTokenLoading] = useState(false);

  // --- Prompt editor state ---
  const [promptTexts, setPromptTexts] = useState<Record<string, PromptTextEntry>>({});
  const [promptConfigs, setPromptConfigs] = useState<Record<string, AgentConfig>>({});
  const [promptChanges, setPromptChanges] = useState<Record<string, string>>({});
  const [selectedPrompt, setSelectedPrompt] = useState<string | null>(null);
  const [promptsLoading, setPromptsLoading] = useState(false);
  const [savingPrompt, setSavingPrompt] = useState(false);

  // --- Intent editor state ---
  const [intentConfig, setIntentConfig] = useState<IntentConfig | null>(null);
  const [intentChanges, setIntentChanges] = useState<IntentConfig | null>(null);
  const [intentsLoading, setIntentsLoading] = useState(false);
  const [savingIntents, setSavingIntents] = useState(false);
  const [expandedIntents, setExpandedIntents] = useState<Set<string>>(new Set());
  const [testMessage, setTestMessage] = useState('');
  const [testResults, setTestResults] = useState<Record<string, IntentTestResult> | null>(null);
  const [testLoading, setTestLoading] = useState(false);
  const [newKeyword, setNewKeyword] = useState<Record<string, string>>({});

  // --- Users (access control) ---
  const [allowedUsers, setAllowedUsers] = useState<AllowedUserRow[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersSaving, setUsersSaving] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserName, setNewUserName] = useState('');
  const [newUserRole, setNewUserRole] = useState<'admin' | 'hr'>('hr');

  // --- General UI ---
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const isAdmin = useAuth.getState().isAdmin;

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  const authHeaders = useCallback(
    () => ({
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    }),
    [token],
  );

  const flash = (msg: string, type: 'success' | 'error' = 'success') => {
    if (type === 'success') {
      setSuccess(msg);
      setError(null);
      setTimeout(() => setSuccess(null), 3000);
    } else {
      setError(msg);
    }
  };

  const getCredVal = <K extends keyof CredentialSettings>(field: K): CredentialSettings[K] =>
    (credChanges[field] !== undefined ? credChanges[field] : creds[field]) as CredentialSettings[K];

  // ---------------------------------------------------------------------------
  // Data fetching
  // ---------------------------------------------------------------------------

  const loadSettings = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await fetch(`${API_BASE}/admin/settings`, { headers: authHeaders() });
      if (clearSessionIf401(res)) return;
      if (!res.ok) throw new Error(`Failed to load settings (${res.status})`);
      const data = await res.json();
      const ai = data.settings?.ai ?? {};
      setCreds({
        azure_openai_endpoint: ai.azure_openai_endpoint,
        azure_openai_key: ai.azure_openai_key,
        azure_openai_deployment: ai.azure_openai_deployment,
        azure_openai_api_version: ai.azure_openai_api_version,
        google_gemini_api_key: ai.google_gemini_api_key,
        google_gemini_model: ai.google_gemini_model,
        anthropic_api_key: ai.anthropic_api_key,
        anthropic_model: ai.anthropic_model,
      });
      setLastUpdated(data.settings?.last_updated ?? '');
      setUpdatedBy(data.settings?.updated_by ?? '');
      setCredChanges({});
      setPublicSite({
        frontend_public_url: data.site?.frontend_public_url ?? '',
        backend_public_url: data.site?.backend_public_url ?? '',
      });
    } catch (err: any) {
      flash(err.message, 'error');
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  const loadAgents = useCallback(async () => {
    try {
      setAgentsLoading(true);
      const res = await fetch(`${API_BASE}/admin/agents`, { headers: authHeaders() });
      if (clearSessionIf401(res)) return;
      if (!res.ok) throw new Error(`Failed to load agents (${res.status})`);
      const data = await res.json();
      setAgents(data.agents ?? {});
      setAgentCount(data.count ?? 0);
      setAgentChanges({});
    } catch (err: any) {
      console.error('Error loading agents:', err);
    } finally {
      setAgentsLoading(false);
    }
  }, [authHeaders]);

  const loadTokenUsage = useCallback(async () => {
    try {
      setTokenLoading(true);
      const res = await fetch(`${API_BASE}/admin/token-usage`, { headers: authHeaders() });
      if (clearSessionIf401(res)) return;
      if (!res.ok) throw new Error(`Failed to load token usage (${res.status})`);
      const data: TokenStats = await res.json();
      setTokenStats(data);
    } catch (err: any) {
      console.error('Error loading token usage:', err);
    } finally {
      setTokenLoading(false);
    }
  }, [authHeaders]);

  const loadPromptTexts = useCallback(async () => {
    try {
      setPromptsLoading(true);
      const res = await fetch(`${API_BASE}/admin/prompts/all-texts`, { headers: authHeaders() });
      if (clearSessionIf401(res)) return;
      if (!res.ok) throw new Error(`Failed to load prompts (${res.status})`);
      const data = await res.json();
      setPromptTexts(data.prompts ?? {});
      setPromptConfigs(data.configs ?? {});
      setPromptChanges({});
    } catch (err: any) {
      console.error('Error loading prompts:', err);
    } finally {
      setPromptsLoading(false);
    }
  }, [authHeaders]);

  const loadIntents = useCallback(async () => {
    try {
      setIntentsLoading(true);
      const res = await fetch(`${API_BASE}/admin/intents`, { headers: authHeaders() });
      if (clearSessionIf401(res)) return;
      if (!res.ok) throw new Error(`Failed to load intents (${res.status})`);
      const data = await res.json();
      setIntentConfig(data.config ?? null);
      setIntentChanges(null);
    } catch (err: any) {
      console.error('Error loading intents:', err);
    } finally {
      setIntentsLoading(false);
    }
  }, [authHeaders]);

  const loadUsers = useCallback(async () => {
    try {
      setUsersLoading(true);
      const res = await fetch(`${API_BASE}/admin/users`, { headers: authHeaders() });
      if (clearSessionIf401(res)) return;
      if (!res.ok) throw new Error(`Failed to load users (${res.status})`);
      const data = await res.json();
      setAllowedUsers(data.users ?? []);
    } catch (err: any) {
      console.error('Error loading users:', err);
      flash(err.message || 'Failed to load users', 'error');
    } finally {
      setUsersLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => {
    if (!isAdmin) {
      setError('Admin access required');
      setLoading(false);
      return;
    }
    loadSettings();
    loadAgents();
    loadTokenUsage();
  }, [isAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  // Lazy-load prompt/intent data when tab switches
  useEffect(() => {
    if (activeTab === 'prompts' && Object.keys(promptTexts).length === 0 && !promptsLoading) {
      loadPromptTexts();
    }
    if (activeTab === 'intents' && !intentConfig && !intentsLoading) {
      loadIntents();
    }
    if (activeTab === 'users') {
      loadUsers();
    }
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---------------------------------------------------------------------------
  // Config tab actions
  // ---------------------------------------------------------------------------

  const handleCredChange = (field: keyof CredentialSettings, value: string) => {
    setCredChanges((prev) => ({ ...prev, [field]: value }));
  };

  const handleAgentChange = (
    agentKey: string,
    field: keyof AgentConfig,
    value: string | number | undefined,
  ) => {
    setAgentChanges((prev) => ({
      ...prev,
      [agentKey]: { ...prev[agentKey], [field]: value },
    }));
  };

  const getAgentVal = <K extends keyof AgentConfig>(
    agentKey: string,
    field: K,
  ): AgentConfig[K] => {
    const change = agentChanges[agentKey]?.[field];
    if (change !== undefined) return change as AgentConfig[K];
    return agents[agentKey]?.[field] as AgentConfig[K];
  };

  const savePublicUrls = async () => {
    try {
      setSavingSiteUrls(true);
      setError(null);
      const res = await fetch(`${API_BASE}/admin/settings`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          frontend_public_url: publicSite.frontend_public_url.trim(),
          backend_public_url: publicSite.backend_public_url.trim(),
        }),
      });
      if (clearSessionIf401(res)) return;
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Failed to save public URLs');
      }
      const data = await res.json();
      setPublicSite({
        frontend_public_url: data.site?.frontend_public_url ?? '',
        backend_public_url: data.site?.backend_public_url ?? '',
      });
      flash('Public URLs saved');
    } catch (err: any) {
      flash(err.message, 'error');
    } finally {
      setSavingSiteUrls(false);
    }
  };

  const saveCredentials = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await fetch(`${API_BASE}/admin/settings`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(credChanges),
      });
      if (clearSessionIf401(res)) return;
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Failed to save credentials');
      }
      const data = await res.json();
      const ai = data.settings?.ai ?? {};
      setCreds({
        azure_openai_endpoint: ai.azure_openai_endpoint,
        azure_openai_key: ai.azure_openai_key,
        azure_openai_deployment: ai.azure_openai_deployment,
        azure_openai_api_version: ai.azure_openai_api_version,
        google_gemini_api_key: ai.google_gemini_api_key,
        google_gemini_model: ai.google_gemini_model,
        anthropic_api_key: ai.anthropic_api_key,
        anthropic_model: ai.anthropic_model,
      });
      setLastUpdated(data.settings?.last_updated ?? '');
      setUpdatedBy(data.settings?.updated_by ?? '');
      setCredChanges({});
      flash('Credentials saved successfully');
    } catch (err: any) {
      flash(err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const saveAgentConfig = async () => {
    try {
      setSavingAgents(true);
      setError(null);
      const merged: Record<string, AgentConfig> = {};
      for (const [key, base] of Object.entries(agents)) {
        merged[key] = { ...base, ...agentChanges[key] };
      }
      const res = await fetch(`${API_BASE}/admin/agents`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ agents: merged }),
      });
      if (clearSessionIf401(res)) return;
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Failed to save agent config');
      }
      await loadAgents();
      flash('Agent configuration saved');
    } catch (err: any) {
      flash(err.message, 'error');
    } finally {
      setSavingAgents(false);
    }
  };

  const reloadFromDisk = async () => {
    try {
      setReloading(true);
      const res = await fetch(`${API_BASE}/admin/prompts/reload`, {
        method: 'POST',
        headers: authHeaders(),
      });
      if (clearSessionIf401(res)) return;
      if (!res.ok) throw new Error('Failed to reload from disk');
      const data = await res.json();
      setLastReload(new Date().toISOString());
      setAgentCount(data.count ?? 0);
      await loadAgents();
      flash(`Reloaded ${data.count ?? 0} agent(s) from disk`);
    } catch (err: any) {
      flash(err.message, 'error');
    } finally {
      setReloading(false);
    }
  };

  const resetSettings = async () => {
    if (!window.confirm('Are you sure you want to reset all settings to defaults? This cannot be undone.'))
      return;
    try {
      setLoading(true);
      setError(null);
      const res = await fetch(`${API_BASE}/admin/settings/reset`, {
        method: 'POST',
        headers: authHeaders(),
      });
      if (clearSessionIf401(res)) return;
      if (!res.ok) throw new Error('Failed to reset settings');
      const data = await res.json();
      const ai = data.settings?.ai ?? {};
      setCreds({
        azure_openai_endpoint: ai.azure_openai_endpoint,
        azure_openai_key: ai.azure_openai_key,
        azure_openai_deployment: ai.azure_openai_deployment,
        azure_openai_api_version: ai.azure_openai_api_version,
        google_gemini_api_key: ai.google_gemini_api_key,
        google_gemini_model: ai.google_gemini_model,
        anthropic_api_key: ai.anthropic_api_key,
        anthropic_model: ai.anthropic_model,
      });
      setLastUpdated(data.settings?.last_updated ?? '');
      setUpdatedBy(data.settings?.updated_by ?? '');
      setCredChanges({});
      flash('Settings reset to defaults');
    } catch (err: any) {
      flash(err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const discardChanges = () => {
    setCredChanges({});
    setAgentChanges({});
    flash('Changes discarded');
  };

  // ---------------------------------------------------------------------------
  // Prompt tab actions
  // ---------------------------------------------------------------------------

  const handlePromptTextChange = (name: string, text: string) => {
    setPromptChanges((prev) => ({ ...prev, [name]: text }));
  };

  const getCurrentPromptText = (name: string): string => {
    if (promptChanges[name] !== undefined) return promptChanges[name];
    return promptTexts[name]?.text ?? '';
  };

  const hasPromptChanged = (name: string): boolean => {
    return promptChanges[name] !== undefined && promptChanges[name] !== (promptTexts[name]?.text ?? '');
  };

  const savePrompt = async (name: string) => {
    try {
      setSavingPrompt(true);
      const text = getCurrentPromptText(name);
      const res = await fetch(`${API_BASE}/admin/prompts/${name}`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ text }),
      });
      if (clearSessionIf401(res)) return;
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Failed to save prompt');
      }
      const data = await res.json();
      setPromptTexts((prev) => ({
        ...prev,
        [name]: { text: data.text, variables: data.variables },
      }));
      setPromptChanges((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
      flash(`Prompt "${name}" saved`);
    } catch (err: any) {
      flash(err.message, 'error');
    } finally {
      setSavingPrompt(false);
    }
  };

  const resetPrompt = (name: string) => {
    setPromptChanges((prev) => {
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  // ---------------------------------------------------------------------------
  // Intent tab actions
  // ---------------------------------------------------------------------------

  const getWorkingIntents = (): IntentConfig => {
    return intentChanges ?? intentConfig ?? { intents: {}, stopwords: [] };
  };

  const updateIntentGroup = (groupName: string, updates: Partial<IntentGroup>) => {
    const current = getWorkingIntents();
    setIntentChanges({
      ...current,
      intents: {
        ...current.intents,
        [groupName]: { ...current.intents[groupName], ...updates },
      },
    });
  };

  const updateStopwords = (words: string[]) => {
    const current = getWorkingIntents();
    setIntentChanges({ ...current, stopwords: words });
  };

  const hasIntentChanges = intentChanges !== null;

  const toggleIntentExpanded = (groupName: string) => {
    setExpandedIntents((prev) => {
      const next = new Set(prev);
      if (next.has(groupName)) next.delete(groupName);
      else next.add(groupName);
      return next;
    });
  };

  const isRegexValid = (pattern: string): boolean => {
    try {
      new RegExp(pattern, 'i');
      return true;
    } catch {
      return false;
    }
  };

  const saveIntents = async () => {
    if (!intentChanges) return;
    try {
      setSavingIntents(true);
      const res = await fetch(`${API_BASE}/admin/intents`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ config: intentChanges }),
      });
      if (clearSessionIf401(res)) return;
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.details ? `Invalid regex: ${err.details.join(', ')}` : err.error || 'Failed to save');
      }
      const data = await res.json();
      setIntentConfig(data.config);
      setIntentChanges(null);
      flash('Intent configuration saved');
    } catch (err: any) {
      flash(err.message, 'error');
    } finally {
      setSavingIntents(false);
    }
  };

  const resetIntents = () => {
    setIntentChanges(null);
    flash('Intent changes discarded');
  };

  const testIntents = async () => {
    if (!testMessage.trim()) return;
    try {
      setTestLoading(true);
      const res = await fetch(`${API_BASE}/admin/intents/test`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ message: testMessage }),
      });
      if (clearSessionIf401(res)) return;
      if (!res.ok) throw new Error('Failed to test intents');
      const data = await res.json();
      setTestResults(data.results);
    } catch (err: any) {
      flash(err.message, 'error');
    } finally {
      setTestLoading(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Derived state
  // ---------------------------------------------------------------------------

  const hasCredChanges = Object.keys(credChanges).length > 0;
  const hasAgentChanges = Object.keys(agentChanges).length > 0;
  const editableAgents = Object.entries(agents).filter(([, cfg]) => !cfg.partial);

  // Build categorized prompt list
  const promptNames = Object.keys(promptTexts).sort();
  const categorizedPrompts: Record<string, string[]> = {};
  for (const [category, prefixes] of Object.entries(PROMPT_CATEGORIES)) {
    if (category === 'Partials') continue;
    categorizedPrompts[category] = promptNames.filter(
      (n) => prefixes.some((p) => n.startsWith(p)) || prefixes.length === 0,
    );
  }
  // Partials: anything not in other categories
  const usedNames = new Set(Object.values(categorizedPrompts).flat());
  categorizedPrompts['Partials'] = promptNames.filter((n) => !usedNames.has(n));
  // Remove empty categories
  for (const key of Object.keys(categorizedPrompts)) {
    if (categorizedPrompts[key].length === 0) delete categorizedPrompts[key];
  }

  // ---------------------------------------------------------------------------
  // Render guards
  // ---------------------------------------------------------------------------

  if (!isAdmin) {
    return (
      <div className="p-8 max-w-5xl mx-auto">
        <div className="bg-red-900/30 border border-red-700 rounded-xl p-4">
          <h2 className="text-xl font-semibold text-red-400">Access Denied</h2>
          <p className="text-red-300 mt-2">Admin access is required to view this page.</p>
        </div>
      </div>
    );
  }

  if (loading && Object.keys(creds).length === 0) {
    return (
      <div className="p-8 max-w-5xl mx-auto">
        <div className="text-center py-16">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-brand-blue" />
          <p className="mt-4 text-dark-text-secondary">Loading settings...</p>
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Tab definitions
  // ---------------------------------------------------------------------------

  const TABS: { id: TabId; label: string; badge?: number }[] = [
    { id: 'config', label: 'Configuration' },
    { id: 'users', label: 'Users' },
    { id: 'prompts', label: 'Prompts', badge: promptNames.length || undefined },
    { id: 'intents', label: 'Intents' },
  ];

  // ---------------------------------------------------------------------------
  // JSX
  // ---------------------------------------------------------------------------

  return (
    <div className="p-8 max-w-5xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-dark-text mb-1">Admin Settings</h1>
        <p className="text-dark-text-secondary">
          Configure API credentials, prompt templates, and intent detection rules.
        </p>
      </div>

      {/* Flash messages */}
      {error && (
        <div className="bg-red-900/30 border border-red-700 rounded-xl p-4">
          <p className="text-red-300">{error}</p>
        </div>
      )}
      {success && (
        <div className="bg-green-900/30 border border-green-700 rounded-xl p-4">
          <p className="text-green-300">{success}</p>
        </div>
      )}

      {/* Tab navigation */}
      <div className="border-b border-dark-border">
        <nav className="flex gap-1" role="tablist">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              role="tab"
              aria-selected={activeTab === tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-brand-blue text-brand-blue'
                  : 'border-transparent text-dark-text-secondary hover:text-dark-text hover:border-dark-border'
              }`}
            >
              {tab.label}
              {tab.badge && (
                <span className="ml-2 px-1.5 py-0.5 text-xs rounded-full bg-dark-bg text-dark-text-secondary">
                  {tab.badge}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* ================================================================== */}
      {/* Configuration Tab                                                   */}
      {/* ================================================================== */}
      {activeTab === 'config' && (
        <div className="space-y-8">
          {/* Last updated */}
          {lastUpdated && (
            <div className="bg-brand-blue/10 border border-brand-blue/30 rounded-xl p-4">
              <p className="text-sm text-dark-text-secondary">
                <span className="font-semibold text-dark-text">Last Updated:</span>{' '}
                {new Date(lastUpdated).toLocaleString()}
                {updatedBy && <> by {updatedBy}</>}
              </p>
            </div>
          )}

          <section className={CARD_CLS}>
            <h2 className="text-xl font-semibold text-dark-text mb-2">Public URLs</h2>
            <p className={`${HINT_CLS} mb-4`}>
              Stored in the database for magic links, profile-update redirects, and candidate-pool Google Sheets OAuth
              return paths when <code className="text-xs">FRONTEND_URL</code> and{' '}
              <code className="text-xs">BACKEND_PUBLIC_URL</code> are not set in the server environment. If those env
              vars are set, they still take precedence.
            </p>
            <div className="space-y-4">
              <div>
                <label className={LABEL_CLS}>Frontend base URL (browser origin)</label>
                <input
                  type="text"
                  value={publicSite.frontend_public_url}
                  onChange={(e) => setPublicSite((s) => ({ ...s, frontend_public_url: e.target.value }))}
                  className={INPUT_CLS}
                  placeholder="https://skilldb.creativedock.com"
                />
              </div>
              <div>
                <label className={LABEL_CLS}>Backend public URL (API origin for OAuth callbacks)</label>
                <input
                  type="text"
                  value={publicSite.backend_public_url}
                  onChange={(e) => setPublicSite((s) => ({ ...s, backend_public_url: e.target.value }))}
                  className={INPUT_CLS}
                  placeholder="Same as frontend if the API is served from the same host"
                />
                <p className={HINT_CLS}>
                  Use a separate value only if your API is on a different host than the web app.
                </p>
              </div>
              <button
                type="button"
                onClick={savePublicUrls}
                disabled={savingSiteUrls}
                className={BTN_PRIMARY}
              >
                {savingSiteUrls ? 'Saving…' : 'Save public URLs'}
              </button>
            </div>
          </section>

          {/* Section 1 – API Credentials */}
          <section className={CARD_CLS}>
            <h2 className="text-xl font-semibold text-dark-text mb-4">API Credentials</h2>
            <div className="space-y-6">
              {/* Azure OpenAI */}
              <div className="space-y-4 border-b border-dark-border pb-6">
                <h3 className="font-semibold text-dark-text">Azure OpenAI</h3>
                <div>
                  <label className={LABEL_CLS}>Endpoint URL</label>
                  <input type="text" value={getCredVal('azure_openai_endpoint') || ''} onChange={(e) => handleCredChange('azure_openai_endpoint', e.target.value)} className={INPUT_CLS} placeholder="https://your-resource.openai.azure.com" />
                </div>
                <div>
                  <label className={LABEL_CLS}>API Key</label>
                  <input type="password" value={getCredVal('azure_openai_key') || ''} onChange={(e) => handleCredChange('azure_openai_key', e.target.value)} className={INPUT_CLS} placeholder="Your Azure OpenAI API key" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={LABEL_CLS}>Deployment Name</label>
                    <input type="text" value={getCredVal('azure_openai_deployment') || ''} onChange={(e) => handleCredChange('azure_openai_deployment', e.target.value)} className={INPUT_CLS} placeholder="gpt-5-chat" />
                  </div>
                  <div>
                    <label className={LABEL_CLS}>API Version</label>
                    <input type="text" value={getCredVal('azure_openai_api_version') || ''} onChange={(e) => handleCredChange('azure_openai_api_version', e.target.value)} className={INPUT_CLS} placeholder="2024-02-15-preview" />
                  </div>
                </div>
              </div>
              {/* Google Gemini */}
              <div className="space-y-4 border-b border-dark-border pb-6">
                <h3 className="font-semibold text-dark-text">Google Gemini</h3>
                <div>
                  <label className={LABEL_CLS}>API Key</label>
                  <input type="password" value={getCredVal('google_gemini_api_key') || ''} onChange={(e) => handleCredChange('google_gemini_api_key', e.target.value)} className={INPUT_CLS} placeholder="Your Google Gemini API key" />
                  <p className={HINT_CLS}>Get your API key from <a href="https://makersuite.google.com/app/apikey" target="_blank" rel="noopener noreferrer" className="text-brand-blue hover:underline">Google AI Studio</a></p>
                </div>
                <div>
                  <label className={LABEL_CLS}>Default Model</label>
                  <select value={getCredVal('google_gemini_model') || 'gemini-2.5-flash'} onChange={(e) => handleCredChange('google_gemini_model', e.target.value)} className={INPUT_CLS}>
                    <option value="gemini-2.5-flash">Gemini 2.5 Flash</option>
                    <option value="gemini-2.5-pro">Gemini 2.5 Pro</option>
                    <option value="gemini-2.0-flash">Gemini 2.0 Flash</option>
                    <option value="gemini-2.0-flash-lite">Gemini 2.0 Flash Lite</option>
                  </select>
                </div>
              </div>
              {/* Anthropic */}
              <div className="space-y-4">
                <h3 className="font-semibold text-dark-text">Anthropic</h3>
                <div>
                  <label className={LABEL_CLS}>API Key</label>
                  <input type="password" value={getCredVal('anthropic_api_key') || ''} onChange={(e) => handleCredChange('anthropic_api_key', e.target.value)} className={INPUT_CLS} placeholder="Your Anthropic API key" />
                  <p className={HINT_CLS}>Get your API key from <a href="https://console.anthropic.com/settings/keys" target="_blank" rel="noopener noreferrer" className="text-brand-blue hover:underline">Anthropic Console</a></p>
                </div>
                <div>
                  <label className={LABEL_CLS}>Default Model</label>
                  <select value={getCredVal('anthropic_model') || 'claude-sonnet-4.5'} onChange={(e) => handleCredChange('anthropic_model', e.target.value)} className={INPUT_CLS}>
                    <option value="claude-haiku-4.5">Claude Haiku 4.5</option>
                    <option value="claude-sonnet-4.5">Claude Sonnet 4.5</option>
                    <option value="claude-opus-4.6">Claude Opus 4.6</option>
                  </select>
                </div>
              </div>
            </div>
          </section>

          {/* Section 2 – Agent Configuration */}
          <section className={CARD_CLS}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-xl font-semibold text-dark-text">Agent Configuration</h2>
                <p className={HINT_CLS}>
                  {editableAgents.length} agent(s) configured
                  {lastReload && <> &middot; last reload {new Date(lastReload).toLocaleTimeString()}</>}
                </p>
              </div>
              <div className="flex gap-2">
                <button onClick={saveAgentConfig} disabled={!hasAgentChanges || savingAgents} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${!hasAgentChanges || savingAgents ? 'bg-dark-border text-dark-text-secondary cursor-not-allowed' : 'bg-brand-blue hover:bg-brand-blue/80 text-white'}`}>
                  {savingAgents ? 'Saving...' : 'Save Agent Config'}
                </button>
                <button onClick={reloadFromDisk} disabled={reloading} className="px-4 py-2 rounded-lg text-sm font-medium bg-brand-blue/20 text-brand-blue border border-brand-blue/30 hover:bg-brand-blue/30 transition-colors disabled:opacity-50">
                  {reloading ? 'Reloading...' : 'Reload from Disk'}
                </button>
              </div>
            </div>

            {agentsLoading ? (
              <p className="text-dark-text-secondary text-sm py-4 text-center">Loading agents...</p>
            ) : editableAgents.length === 0 ? (
              <p className="text-dark-text-secondary text-sm py-4 text-center">No agents found.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left">
                  <thead>
                    <tr className="border-b border-dark-border text-dark-text-secondary">
                      <th className="py-2 pr-3 font-medium whitespace-nowrap">Agent</th>
                      <th className="py-2 pr-3 font-medium whitespace-nowrap">Provider</th>
                      <th className="py-2 pr-3 font-medium whitespace-nowrap">Model</th>
                      <th className="py-2 pr-3 font-medium whitespace-nowrap w-24">Temp</th>
                      <th className="py-2 pr-3 font-medium whitespace-nowrap w-28">Max Tokens</th>
                      <th className="py-2 font-medium whitespace-nowrap">Anonymization</th>
                    </tr>
                  </thead>
                  <tbody>
                    {editableAgents.map(([key, cfg]) => (
                      <tr key={key} className="border-b border-dark-border/50 last:border-b-0 hover:bg-dark-bg/50 transition-colors">
                        <td className="py-2 pr-3 text-dark-text font-medium whitespace-nowrap">{cfg.agent || key}</td>
                        <td className="py-2 pr-3">
                          <select value={getAgentVal(key, 'provider') || ''} onChange={(e) => { const newProvider = (e.target.value || undefined) as AgentConfig['provider']; handleAgentChange(key, 'provider', newProvider); if (newProvider && MODELS_BY_PROVIDER[newProvider]?.length) { handleAgentChange(key, 'model', MODELS_BY_PROVIDER[newProvider][0].value); } else { handleAgentChange(key, 'model', undefined); } }} className={TABLE_SELECT}>
                            <option value="">—</option>
                            {PROVIDER_OPTIONS.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
                          </select>
                        </td>
                        <td className="py-2 pr-3 align-top">
                          {(() => {
                            const provider = getAgentVal(key, 'provider');
                            const currentModel = getAgentVal(key, 'model') ?? '';
                            const options = provider ? MODELS_BY_PROVIDER[provider] ?? [] : [];
                            const isInList = options.some((o) => o.value === currentModel);
                            const selectValue = isInList ? currentModel : (currentModel ? '__custom__' : (options[0]?.value ?? ''));
                            if (!provider) {
                              return (<input type="text" value={currentModel} onChange={(e) => handleAgentChange(key, 'model', e.target.value || undefined)} className={`${TABLE_INPUT} font-mono text-xs`} placeholder="Select provider first" />);
                            }
                            return (
                              <div className="space-y-1">
                                <select value={selectValue} onChange={(e) => { const v = e.target.value; if (v === '__custom__') { handleAgentChange(key, 'model', currentModel || undefined); } else { handleAgentChange(key, 'model', v || undefined); } }} className={TABLE_SELECT}>
                                  {options.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
                                  <option value="__custom__">Custom…</option>
                                </select>
                                {(selectValue === '__custom__' || (!isInList && currentModel)) && (<input type="text" value={currentModel} onChange={(e) => handleAgentChange(key, 'model', e.target.value || undefined)} className={`${TABLE_INPUT} font-mono text-xs w-full`} placeholder="e.g. gpt-5-chat" />)}
                              </div>
                            );
                          })()}
                        </td>
                        <td className="py-2 pr-3">
                          <input type="number" value={getAgentVal(key, 'temperature') ?? ''} onChange={(e) => handleAgentChange(key, 'temperature', e.target.value === '' ? undefined : parseFloat(e.target.value))} className={`${TABLE_INPUT} w-20`} step={0.05} min={0} max={2} placeholder="—" />
                        </td>
                        <td className="py-2 pr-3">
                          <input type="number" value={getAgentVal(key, 'max_tokens') ?? ''} onChange={(e) => handleAgentChange(key, 'max_tokens', e.target.value === '' ? undefined : parseInt(e.target.value, 10))} className={`${TABLE_INPUT} w-24`} step={100} min={100} placeholder="—" />
                        </td>
                        <td className="py-2">
                          <select value={getAgentVal(key, 'anonymize_level') || 'off'} onChange={(e) => handleAgentChange(key, 'anonymize_level', e.target.value as AgentConfig['anonymize_level'])} className={TABLE_SELECT}>
                            {ANON_OPTIONS.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {/* Section 3 – Token Usage */}
          <section className={CARD_CLS}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-dark-text">Token Usage</h2>
              <button onClick={loadTokenUsage} disabled={tokenLoading} className={BTN_SECONDARY}>
                {tokenLoading ? 'Refreshing...' : 'Refresh'}
              </button>
            </div>
            {tokenLoading && !tokenStats ? (
              <p className="text-dark-text-secondary text-sm py-4 text-center">Loading token usage...</p>
            ) : !tokenStats ? (
              <p className="text-dark-text-secondary text-sm py-4 text-center">No token data available.</p>
            ) : (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
                  <StatBox label="Total Tokens" value={tokenStats.total.totalTokens.toLocaleString()} />
                  <StatBox label="Total Calls" value={tokenStats.total.calls.toLocaleString()} />
                  <StatBox label="Tokens / min" value={tokenStats.perMinute.tokensPerMinute.toLocaleString()} />
                  <StatBox label="Avg Duration" value={`${tokenStats.total.avgDurationMs}ms`} />
                  <StatBox label="Prompt Tokens" value={tokenStats.total.promptTokens.toLocaleString()} />
                  <StatBox label="Completion Tokens" value={tokenStats.total.completionTokens.toLocaleString()} />
                  <StatBox label="Calls / min" value={tokenStats.perMinute.callsPerMinute.toLocaleString()} />
                  <StatBox label="Uptime" value={`${tokenStats.total.uptimeMinutes}m`} />
                </div>
                {tokenStats.byProviderModel && tokenStats.byProviderModel.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-dark-text-secondary mb-2 uppercase tracking-wider">Usage by Provider / Model</h3>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-dark-border text-dark-text-secondary text-left">
                            <th className="py-2 pr-4">Provider</th>
                            <th className="py-2 pr-4">Model</th>
                            <th className="py-2 pr-4 text-right">Tokens</th>
                            <th className="py-2 pr-4 text-right">Calls</th>
                            <th className="py-2 pr-4 text-right">Avg Latency</th>
                          </tr>
                        </thead>
                        <tbody>
                          {tokenStats.byProviderModel.map((pm: any, idx: number) => (
                            <tr key={idx} className="border-b border-dark-border/30 hover:bg-dark-bg/50">
                              <td className="py-2 pr-4">
                                <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${pm.provider === 'azure_openai' ? 'bg-blue-900/30 text-blue-400' : pm.provider === 'google_gemini' ? 'bg-green-900/30 text-green-400' : pm.provider === 'anthropic' ? 'bg-orange-900/30 text-orange-400' : 'bg-gray-700 text-gray-300'}`}>
                                  {pm.provider === 'azure_openai' ? 'Azure' : pm.provider === 'google_gemini' ? 'Gemini' : pm.provider === 'anthropic' ? 'Claude' : pm.provider}
                                </span>
                              </td>
                              <td className="py-2 pr-4 text-dark-text font-mono text-xs">{pm.model}</td>
                              <td className="py-2 pr-4 text-right text-dark-text">{pm.totalTokens.toLocaleString()}</td>
                              <td className="py-2 pr-4 text-right text-dark-text">{pm.calls}</td>
                              <td className="py-2 pr-4 text-right text-dark-text-secondary">{pm.avgDurationMs}ms</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </>
            )}
          </section>

          {/* Footer – Action buttons */}
          <div className="flex gap-4 pt-2 pb-8">
            <button onClick={saveCredentials} disabled={!hasCredChanges || loading} className={`px-6 py-3 rounded-lg font-medium transition-colors ${!hasCredChanges || loading ? 'bg-dark-border text-dark-text-secondary cursor-not-allowed' : 'bg-brand-blue hover:bg-brand-blue/80 text-white'}`}>
              {loading ? 'Saving...' : 'Save Credentials'}
            </button>
            <button onClick={discardChanges} disabled={!hasCredChanges && !hasAgentChanges} className="px-6 py-3 rounded-lg font-medium bg-dark-bg border border-dark-border text-dark-text hover:border-dark-text-secondary transition-colors disabled:opacity-50">
              Discard Changes
            </button>
            <button onClick={resetSettings} disabled={loading} className="px-6 py-3 rounded-lg font-medium bg-red-900/30 border border-red-700/50 text-red-400 hover:bg-red-900/50 transition-colors disabled:opacity-50">
              Reset to Defaults
            </button>
          </div>
        </div>
      )}

      {/* ================================================================== */}
      {/* Users Tab — access list (DB); env ALLOWED_USERS / ADMIN_USERS / HR_USERS still seed on deploy */}
      {/* ================================================================== */}
      {activeTab === 'users' && (
        <div className="space-y-6">
          <section className={CARD_CLS}>
            <h2 className="text-xl font-semibold text-dark-text mb-2">Who can access Talent First</h2>
            <p className={`${HINT_CLS} mb-4`}>
              <span className="font-medium text-dark-text-secondary">Only administrators</span> can add, remove, or
              change roles here. Use <strong className="text-dark-text">Admin</strong> for full settings access and{' '}
              <strong className="text-dark-text">HR</strong> for colleagues who should use the app and edit person
              contacts (they do not see this admin area). If the list is empty, anyone from your allowed Google domain
              can still sign in until you add users. Server environment variables{' '}
              <code className="text-xs">ALLOWED_USERS</code>, <code className="text-xs">ADMIN_USERS</code>, and{' '}
              <code className="text-xs">HR_USERS</code> remain supported as a bootstrap fallback on startup.
            </p>

            <div className="flex flex-wrap gap-4 mb-6 pb-6 border-b border-dark-border">
              <div className="flex-1 min-w-[200px]">
                <label className={LABEL_CLS}>Email</label>
                <input
                  type="email"
                  value={newUserEmail}
                  onChange={(e) => setNewUserEmail(e.target.value)}
                  className={INPUT_CLS}
                  placeholder="name@company.com"
                  disabled={usersSaving}
                />
              </div>
              <div className="w-48">
                <label className={LABEL_CLS}>Display name (optional)</label>
                <input
                  type="text"
                  value={newUserName}
                  onChange={(e) => setNewUserName(e.target.value)}
                  className={INPUT_CLS}
                  placeholder="Jane Doe"
                  disabled={usersSaving}
                />
              </div>
              <div className="w-40">
                <label className={LABEL_CLS}>Role</label>
                <select
                  value={newUserRole}
                  onChange={(e) => setNewUserRole(e.target.value as 'admin' | 'hr')}
                  className={INPUT_CLS}
                  disabled={usersSaving}
                >
                  <option value="hr">HR</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div className="flex items-end">
                <button
                  type="button"
                  onClick={async () => {
                    const email = newUserEmail.trim().toLowerCase();
                    if (!email.includes('@')) {
                      flash('Enter a valid email address', 'error');
                      return;
                    }
                    try {
                      setUsersSaving(true);
                      const res = await fetch(`${API_BASE}/admin/users`, {
                        method: 'POST',
                        headers: authHeaders(),
                        body: JSON.stringify({
                          email,
                          name: newUserName.trim() || null,
                          role: newUserRole,
                        }),
                      });
                      if (clearSessionIf401(res)) return;
                      const data = await res.json().catch(() => ({}));
                      if (!res.ok) throw new Error(data.error || 'Failed to add user');
                      flash(`Saved access for ${email}`);
                      setNewUserEmail('');
                      setNewUserName('');
                      setNewUserRole('hr');
                      await loadUsers();
                    } catch (e: any) {
                      flash(e.message || 'Failed to add user', 'error');
                    } finally {
                      setUsersSaving(false);
                    }
                  }}
                  disabled={usersSaving || !newUserEmail.trim()}
                  className={BTN_PRIMARY}
                >
                  {usersSaving ? 'Saving…' : 'Add user'}
                </button>
              </div>
            </div>

            {usersLoading ? (
              <p className="text-dark-text-secondary text-sm py-6 text-center">Loading users…</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left border-collapse">
                  <thead>
                    <tr className="border-b border-dark-border text-dark-text-secondary">
                      <th className="py-2 pr-4 font-medium">Email</th>
                      <th className="py-2 pr-4 font-medium">Name</th>
                      <th className="py-2 pr-4 font-medium">Role</th>
                      <th className="py-2 pr-4 font-medium">Status</th>
                      <th className="py-2 font-medium text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allowedUsers.map((row) => (
                      <tr
                        key={row.id}
                        className={`border-b border-dark-border/80 ${row.is_active === 0 ? 'opacity-60' : ''}`}
                      >
                        <td className="py-2 pr-4 text-dark-text font-mono text-xs">{row.email}</td>
                        <td className="py-2 pr-4 text-dark-text-secondary">{row.name || '—'}</td>
                        <td className="py-2 pr-4">
                          <select
                            value={row.role}
                            onChange={async (e) => {
                              const role = e.target.value as 'admin' | 'hr' | 'user';
                              try {
                                setUsersSaving(true);
                                const res = await fetch(`${API_BASE}/admin/users`, {
                                  method: 'POST',
                                  headers: authHeaders(),
                                  body: JSON.stringify({
                                    email: row.email,
                                    name: row.name,
                                    role,
                                  }),
                                });
                                if (clearSessionIf401(res)) return;
                                const data = await res.json().catch(() => ({}));
                                if (!res.ok) throw new Error(data.error || 'Failed to update role');
                                await loadUsers();
                              } catch (err: any) {
                                flash(err.message || 'Update failed', 'error');
                              } finally {
                                setUsersSaving(false);
                              }
                            }}
                            disabled={usersSaving || row.is_active === 0}
                            className={TABLE_SELECT}
                          >
                            <option value="hr">HR</option>
                            <option value="admin">Admin</option>
                            <option value="user">User (legacy)</option>
                          </select>
                        </td>
                        <td className="py-2 pr-4">
                          {row.is_active === 1 ? (
                            <span className="text-green-400">Active</span>
                          ) : (
                            <span className="text-dark-text-secondary">Inactive</span>
                          )}
                        </td>
                        <td className="py-2 text-right space-x-2">
                          {row.is_active === 0 ? (
                            <button
                              type="button"
                              className="text-sm text-brand-blue hover:underline"
                              disabled={usersSaving}
                              onClick={async () => {
                                try {
                                  setUsersSaving(true);
                                  const res = await fetch(`${API_BASE}/admin/users`, {
                                    method: 'POST',
                                    headers: authHeaders(),
                                    body: JSON.stringify({
                                      email: row.email,
                                      name: row.name,
                                      role: row.role,
                                    }),
                                  });
                                  if (clearSessionIf401(res)) return;
                                  const data = await res.json().catch(() => ({}));
                                  if (!res.ok) throw new Error(data.error || 'Failed to reactivate');
                                  await loadUsers();
                                } catch (err: any) {
                                  flash(err.message || 'Reactivate failed', 'error');
                                } finally {
                                  setUsersSaving(false);
                                }
                              }}
                            >
                              Reactivate
                            </button>
                          ) : (
                            <button
                              type="button"
                              className="text-sm text-red-400 hover:underline"
                              disabled={usersSaving}
                              onClick={async () => {
                                if (!window.confirm(`Remove access for ${row.email}? They can be re-added later.`)) {
                                  return;
                                }
                                try {
                                  setUsersSaving(true);
                                  const res = await fetch(
                                    `${API_BASE}/admin/users/${encodeURIComponent(row.email)}`,
                                    { method: 'DELETE', headers: authHeaders() },
                                  );
                                  if (clearSessionIf401(res)) return;
                                  const data = await res.json().catch(() => ({}));
                                  if (!res.ok) throw new Error(data.error || 'Failed to remove');
                                  await loadUsers();
                                  flash('User access removed');
                                } catch (err: any) {
                                  flash(err.message || 'Remove failed', 'error');
                                } finally {
                                  setUsersSaving(false);
                                }
                              }}
                            >
                              Remove
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {allowedUsers.length === 0 && (
                  <p className="text-dark-text-secondary text-sm py-4 text-center">No rows yet — add users above.</p>
                )}
              </div>
            )}
          </section>
        </div>
      )}

      {/* ================================================================== */}
      {/* Prompts Tab                                                         */}
      {/* ================================================================== */}
      {activeTab === 'prompts' && (
        <div className="flex gap-6 min-h-[600px]">
          {/* Left panel – prompt list */}
          <div className="w-1/3 space-y-4">
            <h2 className="text-lg font-semibold text-dark-text">Prompt Templates</h2>
            {promptsLoading ? (
              <p className="text-dark-text-secondary text-sm py-4 text-center">Loading prompts...</p>
            ) : (
              <div className="space-y-3 max-h-[600px] overflow-y-auto pr-1">
                {Object.entries(categorizedPrompts).map(([category, names]) => (
                  <div key={category}>
                    <p className="text-xs font-semibold text-dark-text-secondary uppercase tracking-wider mb-1">{category}</p>
                    {names.map((name) => (
                      <button
                        key={name}
                        onClick={() => setSelectedPrompt(name)}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                          selectedPrompt === name
                            ? 'bg-brand-blue/20 text-brand-blue border border-brand-blue/30'
                            : 'text-dark-text hover:bg-dark-bg border border-transparent'
                        } ${hasPromptChanged(name) ? 'font-semibold' : ''}`}
                      >
                        <span className="font-mono text-xs">{name}</span>
                        {hasPromptChanged(name) && <span className="ml-1 text-yellow-400">*</span>}
                        {promptTexts[name]?.variables?.length > 0 && (
                          <span className="ml-2 text-xs text-dark-text-secondary">
                            {promptTexts[name].variables.length} var{promptTexts[name].variables.length > 1 ? 's' : ''}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Right panel – prompt editor */}
          <div className="w-2/3">
            {!selectedPrompt ? (
              <div className="flex items-center justify-center h-full text-dark-text-secondary">
                <p>Select a prompt from the list to edit</p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Agent config summary */}
                {promptConfigs[selectedPrompt] && !promptConfigs[selectedPrompt].partial && (
                  <div className="bg-dark-bg rounded-lg border border-dark-border/50 p-3 flex items-center gap-4 text-sm">
                    <span className="text-dark-text-secondary">Agent:</span>
                    <span className="text-dark-text font-medium">{selectedPrompt}</span>
                    <span className="text-dark-text-secondary">|</span>
                    <span className="text-dark-text-secondary">Provider:</span>
                    <span className="text-dark-text">{promptConfigs[selectedPrompt]?.provider || '—'}</span>
                    <span className="text-dark-text-secondary">|</span>
                    <span className="text-dark-text-secondary">Model:</span>
                    <span className="text-dark-text font-mono text-xs">{promptConfigs[selectedPrompt]?.model || '—'}</span>
                    <span className="text-dark-text-secondary">|</span>
                    <span className="text-dark-text-secondary">Temp:</span>
                    <span className="text-dark-text">{promptConfigs[selectedPrompt]?.temperature ?? '—'}</span>
                  </div>
                )}

                {/* Variables */}
                {(promptChanges[selectedPrompt] !== undefined
                  ? extractVars(promptChanges[selectedPrompt])
                  : promptTexts[selectedPrompt]?.variables ?? []
                ).length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    <span className="text-xs text-dark-text-secondary self-center">Variables:</span>
                    {(promptChanges[selectedPrompt] !== undefined
                      ? extractVars(promptChanges[selectedPrompt])
                      : promptTexts[selectedPrompt]?.variables ?? []
                    ).map((v) => (
                      <span key={v} className="px-2 py-0.5 bg-brand-blue/10 text-brand-blue border border-brand-blue/20 rounded text-xs font-mono">
                        {'{{' + v + '}}'}
                      </span>
                    ))}
                  </div>
                )}

                {/* Textarea */}
                <textarea
                  value={getCurrentPromptText(selectedPrompt)}
                  onChange={(e) => handlePromptTextChange(selectedPrompt, e.target.value)}
                  className="w-full h-[400px] px-4 py-3 bg-dark-bg border border-dark-border rounded-lg text-dark-text font-mono text-sm resize-y focus:outline-none focus:ring-2 focus:ring-brand-blue"
                  spellCheck={false}
                />

                {/* Char/line count */}
                <div className="flex items-center justify-between">
                  <p className="text-xs text-dark-text-secondary">
                    {getCurrentPromptText(selectedPrompt).length} chars &middot;{' '}
                    {getCurrentPromptText(selectedPrompt).split('\n').length} lines
                  </p>
                  <div className="flex gap-2">
                    <button onClick={() => resetPrompt(selectedPrompt)} disabled={!hasPromptChanged(selectedPrompt)} className={BTN_SECONDARY}>
                      Reset to Disk
                    </button>
                    <button onClick={() => savePrompt(selectedPrompt)} disabled={!hasPromptChanged(selectedPrompt) || savingPrompt} className={BTN_PRIMARY}>
                      {savingPrompt ? 'Saving...' : 'Save Prompt'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ================================================================== */}
      {/* Intents Tab                                                         */}
      {/* ================================================================== */}
      {activeTab === 'intents' && (
        <div className="space-y-6">
          {/* Header actions */}
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-dark-text">Intent Detection Rules</h2>
            <div className="flex gap-2">
              <button onClick={resetIntents} disabled={!hasIntentChanges} className={BTN_SECONDARY}>
                Reset to Disk
              </button>
              <button onClick={saveIntents} disabled={!hasIntentChanges || savingIntents} className={BTN_PRIMARY}>
                {savingIntents ? 'Saving...' : 'Save Intents'}
              </button>
            </div>
          </div>

          {intentsLoading ? (
            <p className="text-dark-text-secondary text-sm py-8 text-center">Loading intent configuration...</p>
          ) : (
            <>
              {/* Intent groups */}
              <div className="space-y-3">
                {Object.entries(getWorkingIntents().intents).map(([groupName, group]) => (
                  <div key={groupName} className={CARD_CLS}>
                    {/* Accordion header */}
                    <button
                      onClick={() => toggleIntentExpanded(groupName)}
                      className="w-full flex items-center justify-between text-left"
                    >
                      <div>
                        <h3 className="text-dark-text font-semibold">{groupName.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}</h3>
                        {group.description && <p className="text-xs text-dark-text-secondary mt-0.5">{group.description}</p>}
                      </div>
                      <div className="flex items-center gap-3 text-dark-text-secondary text-xs">
                        {group.keywords && <span>{group.keywords.length} keywords</span>}
                        {group.regex_patterns && <span>{Object.keys(group.regex_patterns).length} regex</span>}
                        {group.patterns && <span>{group.patterns.length} patterns</span>}
                        <span className="text-lg">{expandedIntents.has(groupName) ? '−' : '+'}</span>
                      </div>
                    </button>

                    {/* Expanded content */}
                    {expandedIntents.has(groupName) && (
                      <div className="mt-4 space-y-4 border-t border-dark-border pt-4">
                        {/* Keywords */}
                        {group.keywords && (
                          <div>
                            <label className="text-sm font-medium text-dark-text mb-2 block">Keywords</label>
                            <div className="flex flex-wrap gap-1.5 mb-2">
                              {group.keywords.map((kw, i) => (
                                <span key={i} className="inline-flex items-center gap-1 px-2 py-1 bg-dark-bg border border-dark-border rounded text-xs text-dark-text">
                                  {kw}
                                  <button
                                    onClick={() => {
                                      const updated = group.keywords!.filter((_, j) => j !== i);
                                      updateIntentGroup(groupName, { keywords: updated });
                                    }}
                                    className="text-dark-text-secondary hover:text-red-400 ml-0.5"
                                  >
                                    &times;
                                  </button>
                                </span>
                              ))}
                            </div>
                            <div className="flex gap-2">
                              <input
                                type="text"
                                value={newKeyword[groupName] ?? ''}
                                onChange={(e) => setNewKeyword((prev) => ({ ...prev, [groupName]: e.target.value }))}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' && newKeyword[groupName]?.trim()) {
                                    updateIntentGroup(groupName, { keywords: [...(group.keywords || []), newKeyword[groupName].trim()] });
                                    setNewKeyword((prev) => ({ ...prev, [groupName]: '' }));
                                  }
                                }}
                                className={`${TABLE_INPUT} flex-1`}
                                placeholder="Type keyword and press Enter"
                              />
                            </div>
                          </div>
                        )}

                        {/* Team keywords extra */}
                        {group.team_keywords_extra && (
                          <div>
                            <label className="text-sm font-medium text-dark-text mb-2 block">Team Keywords (Extra)</label>
                            <div className="flex flex-wrap gap-1.5 mb-2">
                              {group.team_keywords_extra.map((kw, i) => (
                                <span key={i} className="inline-flex items-center gap-1 px-2 py-1 bg-dark-bg border border-dark-border rounded text-xs text-dark-text">
                                  {kw}
                                  <button
                                    onClick={() => {
                                      const updated = group.team_keywords_extra!.filter((_, j) => j !== i);
                                      updateIntentGroup(groupName, { team_keywords_extra: updated });
                                    }}
                                    className="text-dark-text-secondary hover:text-red-400 ml-0.5"
                                  >
                                    &times;
                                  </button>
                                </span>
                              ))}
                            </div>
                            <div className="flex gap-2">
                              <input
                                type="text"
                                value={newKeyword[`${groupName}_extra`] ?? ''}
                                onChange={(e) => setNewKeyword((prev) => ({ ...prev, [`${groupName}_extra`]: e.target.value }))}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' && newKeyword[`${groupName}_extra`]?.trim()) {
                                    updateIntentGroup(groupName, { team_keywords_extra: [...(group.team_keywords_extra || []), newKeyword[`${groupName}_extra`].trim()] });
                                    setNewKeyword((prev) => ({ ...prev, [`${groupName}_extra`]: '' }));
                                  }
                                }}
                                className={`${TABLE_INPUT} flex-1`}
                                placeholder="Type keyword and press Enter"
                              />
                            </div>
                          </div>
                        )}

                        {/* Regex patterns */}
                        {group.regex_patterns && (
                          <div>
                            <label className="text-sm font-medium text-dark-text mb-2 block">Regex Patterns</label>
                            <div className="space-y-2">
                              {Object.entries(group.regex_patterns).map(([label, pattern]) => (
                                <div key={label} className="flex items-center gap-2">
                                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${isRegexValid(pattern) ? 'bg-green-500' : 'bg-red-500'}`} />
                                  <span className="text-xs text-dark-text-secondary w-36 flex-shrink-0 font-mono">{label}</span>
                                  <input
                                    type="text"
                                    value={pattern}
                                    onChange={(e) => {
                                      updateIntentGroup(groupName, {
                                        regex_patterns: { ...group.regex_patterns, [label]: e.target.value },
                                      });
                                    }}
                                    className={`${TABLE_INPUT} flex-1 font-mono text-xs`}
                                  />
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Greeting patterns */}
                        {group.patterns && (
                          <div>
                            <label className="text-sm font-medium text-dark-text mb-2 block">Patterns</label>
                            <div className="space-y-2">
                              {group.patterns.map((pattern, i) => (
                                <div key={i} className="flex items-center gap-2">
                                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${isRegexValid(pattern) ? 'bg-green-500' : 'bg-red-500'}`} />
                                  <input
                                    type="text"
                                    value={pattern}
                                    onChange={(e) => {
                                      const updated = [...group.patterns!];
                                      updated[i] = e.target.value;
                                      updateIntentGroup(groupName, { patterns: updated });
                                    }}
                                    className={`${TABLE_INPUT} flex-1 font-mono text-xs`}
                                  />
                                  <button
                                    onClick={() => {
                                      const updated = group.patterns!.filter((_, j) => j !== i);
                                      updateIntentGroup(groupName, { patterns: updated });
                                    }}
                                    className="text-dark-text-secondary hover:text-red-400 text-sm"
                                  >
                                    &times;
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* False positives / exclude words */}
                        {(group.false_positives || group.exclude_words) && (
                          <div>
                            <label className="text-sm font-medium text-dark-text mb-2 block">
                              {group.false_positives ? 'False Positives' : 'Exclude Words'}
                            </label>
                            <div className="flex flex-wrap gap-1.5">
                              {(group.false_positives || group.exclude_words || []).map((word, i) => (
                                <span key={i} className="inline-flex items-center gap-1 px-2 py-1 bg-red-900/20 border border-red-700/30 rounded text-xs text-red-300">
                                  {word}
                                  <button
                                    onClick={() => {
                                      const field = group.false_positives ? 'false_positives' : 'exclude_words';
                                      const updated = (group[field] || []).filter((_, j) => j !== i);
                                      updateIntentGroup(groupName, { [field]: updated });
                                    }}
                                    className="hover:text-red-400 ml-0.5"
                                  >
                                    &times;
                                  </button>
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}

                {/* Stopwords section */}
                <div className={CARD_CLS}>
                  <button
                    onClick={() => toggleIntentExpanded('__stopwords__')}
                    className="w-full flex items-center justify-between text-left"
                  >
                    <div>
                      <h3 className="text-dark-text font-semibold">Stopwords</h3>
                      <p className="text-xs text-dark-text-secondary mt-0.5">Words filtered out during search scoring</p>
                    </div>
                    <div className="flex items-center gap-3 text-dark-text-secondary text-xs">
                      <span>{getWorkingIntents().stopwords.length} words</span>
                      <span className="text-lg">{expandedIntents.has('__stopwords__') ? '−' : '+'}</span>
                    </div>
                  </button>
                  {expandedIntents.has('__stopwords__') && (
                    <div className="mt-4 border-t border-dark-border pt-4">
                      <div className="flex flex-wrap gap-1.5 mb-2">
                        {getWorkingIntents().stopwords.map((word, i) => (
                          <span key={i} className="inline-flex items-center gap-1 px-2 py-1 bg-dark-bg border border-dark-border rounded text-xs text-dark-text">
                            {word}
                            <button
                              onClick={() => {
                                const updated = getWorkingIntents().stopwords.filter((_, j) => j !== i);
                                updateStopwords(updated);
                              }}
                              className="text-dark-text-secondary hover:text-red-400 ml-0.5"
                            >
                              &times;
                            </button>
                          </span>
                        ))}
                      </div>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={newKeyword['__stopwords__'] ?? ''}
                          onChange={(e) => setNewKeyword((prev) => ({ ...prev, ['__stopwords__']: e.target.value }))}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && newKeyword['__stopwords__']?.trim()) {
                              updateStopwords([...getWorkingIntents().stopwords, newKeyword['__stopwords__'].trim()]);
                              setNewKeyword((prev) => ({ ...prev, ['__stopwords__']: '' }));
                            }
                          }}
                          className={`${TABLE_INPUT} flex-1`}
                          placeholder="Type stopword and press Enter"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Test Panel */}
              <div className={CARD_CLS}>
                <h3 className="text-dark-text font-semibold mb-3">Test Intent Detection</h3>
                <div className="flex gap-2 mb-4">
                  <input
                    type="text"
                    value={testMessage}
                    onChange={(e) => setTestMessage(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') testIntents(); }}
                    className={`${INPUT_CLS} flex-1`}
                    placeholder="Type a test message..."
                  />
                  <button onClick={testIntents} disabled={testLoading || !testMessage.trim()} className={BTN_PRIMARY}>
                    {testLoading ? 'Testing...' : 'Test Intents'}
                  </button>
                </div>

                {testResults && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-dark-border text-dark-text-secondary text-left">
                          <th className="py-2 pr-4">Intent</th>
                          <th className="py-2 pr-4 text-center">Fired</th>
                          <th className="py-2 pr-4 text-right">Confidence</th>
                          <th className="py-2">Matched</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(testResults).map(([name, result]) => (
                          <tr key={name} className="border-b border-dark-border/30">
                            <td className="py-2 pr-4 text-dark-text font-medium">{name.replace(/_/g, ' ')}</td>
                            <td className="py-2 pr-4 text-center">
                              <span className={`inline-block w-3 h-3 rounded-full ${result.fired ? 'bg-green-500' : 'bg-dark-border'}`} />
                            </td>
                            <td className="py-2 pr-4 text-right font-mono text-xs">
                              <span className={result.confidence > 50 ? 'text-green-400' : result.confidence > 0 ? 'text-yellow-400' : 'text-dark-text-secondary'}>
                                {result.confidence}%
                              </span>
                            </td>
                            <td className="py-2 text-xs text-dark-text-secondary">
                              {result.matched.length > 0 ? result.matched.join(', ') : '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function extractVars(text: string): string[] {
  const matches = text.match(/\{\{(\w+)\}\}/g) || [];
  return [...new Set(matches.map((m) => m.slice(2, -2)))];
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const StatBox: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="bg-dark-bg rounded-lg border border-dark-border/50 p-3">
    <p className="text-dark-text-secondary text-xs mb-1">{label}</p>
    <p className="text-dark-text font-semibold text-lg">{value}</p>
  </div>
);
